/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { useEffect, useState } from 'react';

import { QuickMenuShortcut } from 'client-code/homeshortcut';

import {
    PRIMARY_BRANDING_LOGO_HEIGHT_1080P,
    PRIMARY_BRANDING_LOGO_WIDTH_1080P,
    SIDEBAR_TIMEOUT,
} from '../../config';
import { BrandingUiInfoKeys } from '../../core/entities/BrandingUiInfoKeys';
import { ServicesClientImpl } from '../../core/interactors';
import { QuickMenuShortcutInteractor } from '../../core/interactors/QuickMenuShortcutInteractor';
import { Locale, LocaleKeys } from '../../core/locale';
import { NetworkManager } from '../../core/network/NetworkManager';
import { RootState, useTivoDispatch, useTivoSelector } from '../../core/tivo/tivo-redux-wrapper';
import { ListTypes } from '../../core/utils';
import { hideOverlayDialog } from '../../store/overlaydialog/overlaydialog.reducers';
import {
    MOUSE_TRAP_ID,
    VALUE_SEPERATOR,
} from '../../store/sidebarnavigationmenu/sidebarnavigationmenu.constants';
import { hideSidebarNavigationMenu } from '../../store/sidebarnavigationmenu/sidebarnavigationmenu.reducers';
import { VisibilityState } from '../../store/visibilityevents/visibilitystate';
import { List, Mousetrap } from '../common';
import { ImageLoader } from '../ImageLoader';
import { ImageLoaderTypes } from '../ImageLoader/ImageLoaderTypes';

import './SidebarNavigationMenu.scss';

const SidebarNavigationMenu = () => {
    const services = new ServicesClientImpl();
    const dispatch = useTivoDispatch();
    const primaryBrandingImageUrl = services.getPartnerBrandingImageUrl(
        PRIMARY_BRANDING_LOGO_WIDTH_1080P,
        PRIMARY_BRANDING_LOGO_HEIGHT_1080P,
    );
    const secondaryBranding = services.getAppUiImageByKey(
        BrandingUiInfoKeys.APP_ICONS.SECONDARY_BRANDING,
    );

    const { isSidebarNavigationMenuActive } = useTivoSelector(
        (state: RootState) => state.sidebarNavigationMenu,
    );

    const { visibilityState } = useTivoSelector((state: RootState) => state.visibilityEvents);

    const { isOverlayDialogActive } = useTivoSelector((state: RootState) => state.overlayDialog);

    const [list, setList] = useState<Array<QuickMenuShortcut>>([]);

    const shortcutIconList = [
        {
            iconName: Locale.getString(LocaleKeys.SIDEBAR_NAVIGATION_MENU_GUIDE_OPTION_TEXT),
            iconClass: {
                unhighlighted: 'guide-icon',
                highlighted: 'guide-icon-highlighted',
            },
            isDisabled: false,
        },
        {
            iconName: Locale.getString(LocaleKeys.SIDEBAR_NAVIGATION_MENU_ON_DEMAND_OPTION_TEXT),
            iconClass: {
                unhighlighted: 'on-demand-icon',
                highlighted: 'on-demand-icon-highlighted',
            },
            isDisabled: NetworkManager.isNetworkDisconnected(),
        },
        {
            iconName: Locale.getString(LocaleKeys.SIDEBAR_NAVIGATION_MENU_MY_SHOWS_OPTION_TEXT),
            iconClass: {
                unhighlighted: 'my-shows-icon',
                highlighted: 'my-shows-icon-highlighted',
            },
            isDisabled: NetworkManager.isNetworkDisconnected(),
        },
        {
            iconName: Locale.getString(LocaleKeys.SIDEBAR_NAVIGATION_MENU_SETTINGS_OPTION_TEXT),
            iconClass: {
                unhighlighted: 'settings-icon',
                highlighted: 'settings-icon-highlighted',
            },
            isDisabled: false,
        },
    ];

    const renderItem = (item, itemClass, index, _, position) => {
        return (
            <Mousetrap
                key={`filter-item-${index}`}
                value={`${index}${VALUE_SEPERATOR}${ListTypes.SIDEBAR_MENU_ITEMS}`}
            >
                <div className={`menu-option ${itemClass}`}>
                    <>
                        {position === index ? (
                            <img className={`option-icon ${item?.iconClass?.highlighted}`} />
                        ) : (
                            <img className={`option-icon ${item?.iconClass?.unhighlighted}`} />
                        )}
                    </>
                    <div className={`option-text ${item?.isDisabled ? 'disabled' : ''}`}>
                        {item?.title}
                    </div>
                </div>
            </Mousetrap>
        );
    };

    useEffect(() => {
        const quickMenuService = new QuickMenuShortcutInteractor();

        // Refresh icon/disabled state on network changes using whatever is cached.
        const cachedShortcuts = quickMenuService.getCachedQuickMenuShortcuts();
        setList(quickMenuService.addIconUrlsToShortcuts(cachedShortcuts, shortcutIconList));
    }, [NetworkManager.isNetworkDisconnected()]);

    useEffect(() => {
        if (!isSidebarNavigationMenuActive) return;

        const quickMenuService = new QuickMenuShortcutInteractor();

        quickMenuService.fetchQuickMenuShortcutsWithFallback().then((shortcuts) => {
            setList(quickMenuService.addIconUrlsToShortcuts(shortcuts, shortcutIconList));
        });
    }, [isSidebarNavigationMenuActive]);

    useEffect(() => {
        if (visibilityState === VisibilityState.ON_PAUSE) {
            if (isOverlayDialogActive) dispatch(hideOverlayDialog());
            if (isSidebarNavigationMenuActive) dispatch(hideSidebarNavigationMenu());
        }
    }, [visibilityState]);

    useEffect(() => {
        let timerId: NodeJS.Timeout;
        if (isSidebarNavigationMenuActive && !isOverlayDialogActive) {
            timerId = setTimeout(() => {
                dispatch(hideSidebarNavigationMenu());
            }, SIDEBAR_TIMEOUT);
        }

        return () => {
            clearTimeout(timerId);
        };
    }, [isSidebarNavigationMenuActive, isOverlayDialogActive]);

    const menuDiv = (
        <List
            type={ListTypes.SIDEBAR_MENU_ITEMS}
            itemList={list}
            initialFocusedIndex={0}
            initialSelectedIndex={0}
            generateItemView={(item, itemclass, index, _, position) =>
                renderItem(item, itemclass, index, _, position)
            }
        />
    );

    return isSidebarNavigationMenuActive ? (
        <div className="sidebar-navigation-container">
            <Mousetrap className="dimmer" value={MOUSE_TRAP_ID}>
                <div className="branding-image">
                    <ImageLoader
                        src={primaryBrandingImageUrl}
                        imageType={ImageLoaderTypes.SCREEN_HEADER_PRIMARY_BRANDING}
                    />
                </div>
                <div className="secondary-branding-image">
                    <img src={secondaryBranding?.imageUrl} />
                </div>
            </Mousetrap>
            {menuDiv}
        </div>
    ) : null;
};

export default SidebarNavigationMenu;
